/**
 * `createApiClient(options)` — typed `openapi-fetch` wrapper for Sparta.
 *
 * Wraps `openapi-fetch` with:
 *
 * - CSRF middleware: every mutating method (POST/PUT/PATCH/DELETE) gets
 *   the cached `x-csrf-token` header. On 403 with a CSRF error code,
 *   the cache is cleared and the request retried ONCE with a fresh
 *   token. Safe methods (GET/HEAD/OPTIONS) skip the header.
 *
 * - Critical-endpoint drift detection: on every successful response,
 *   if the (method, path) pair is in the registry, the response body
 *   is `safeParse`d against the registered Zod schema; failures invoke
 *   the consumer-supplied `onDrift` reporter (apps/admin wires this to
 *   `Sentry.captureException`).
 *
 * Phase 0 wires the middleware. Phase 1 features call
 * `registerCriticalEndpoint` to populate the registry.
 */
import createClient from 'openapi-fetch';
import { csrfHeaders } from '@artemis/contracts';
import { getCsrfToken, clearCsrfTokenCache } from './csrf-cache.js';
import { checkCriticalEndpointResponse, } from './critical-endpoints.js';
const MUTATING_METHODS = new Set([
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
]);
const noopDrift = () => {
    // Default: silent. Consumers wire Sentry; tests inject spies.
};
export function createApiClient(options) {
    const fetchImpl = options.fetchImpl ?? fetch;
    const onDrift = options.onDrift ?? noopDrift;
    const csrfMiddleware = {
        async onRequest({ request }) {
            const method = request.method.toUpperCase();
            if (!MUTATING_METHODS.has(method)) {
                return request;
            }
            const token = await getCsrfToken(options.baseUrl, fetchImpl);
            const headers = csrfHeaders(token);
            for (const [k, v] of Object.entries(headers)) {
                request.headers.set(k, v);
            }
            return request;
        },
        async onResponse({ request, response }) {
            const method = request.method.toUpperCase();
            if (response.status === 403 &&
                MUTATING_METHODS.has(method) &&
                !request.headers.has('x-csrf-retry')) {
                // Clone the body before reading for the error sniff so the
                // downstream consumer can still parse the response.
                //
                // Backend envelope (apps/api/src/common/guards/csrf.guard.ts:82):
                //   throw new ForbiddenException('CSRF_TOKEN_INVALID')
                // → NestJS default body { statusCode: 403, message: 'CSRF_TOKEN_INVALID',
                //   error: 'Forbidden' }, then GlobalExceptionFilter spreads + adds
                //   `requestId`. The slug lives in `message`, NOT `code`.
                // We additionally accept `code` for forward-compat in case the
                // exception filter is later widened to surface a typed code field.
                const cloned = response.clone();
                let isCsrfError = false;
                try {
                    const body = (await cloned.json());
                    isCsrfError =
                        body.message === 'CSRF_TOKEN_INVALID' ||
                            body.code === 'CSRF_TOKEN_INVALID';
                }
                catch {
                    // Some 403s from @fastify/csrf-protection may not carry JSON.
                    // Fail open on retry so a transient CSRF rotation succeeds.
                    isCsrfError = true;
                }
                if (isCsrfError) {
                    clearCsrfTokenCache();
                    const fresh = await getCsrfToken(options.baseUrl, fetchImpl);
                    const retryHeaders = new Headers(request.headers);
                    retryHeaders.set('x-csrf-token', fresh);
                    retryHeaders.set('x-csrf-retry', '1');
                    return fetchImpl(request.url, {
                        method: request.method,
                        headers: retryHeaders,
                        body: request.body,
                        credentials: 'include',
                    });
                }
            }
            return response;
        },
    };
    const driftMiddleware = {
        async onResponse({ request, response }) {
            if (!response.ok) {
                return response;
            }
            const url = new URL(request.url);
            // openapi-fetch passes the templated path on the request through
            // the request.headers.get('x-openapi-path') only when configured;
            // since we cannot reliably reconstruct the template from the
            // concrete URL, we match on pathname directly. Critical endpoints
            // register their templated path; templates with parameters use the
            // OpenAPI template form (e.g. `/organizations/{slug}`) and the
            // matcher in checkCriticalEndpointResponse compares concrete vs
            // template via a future enhancement. For now the registry is
            // matched on exact pathname — Phase 1 features that need
            // parameterised matching will extend the matcher.
            const cloned = response.clone();
            let body = null;
            try {
                body = await cloned.json();
            }
            catch {
                return response; // non-JSON response — nothing to validate.
            }
            checkCriticalEndpointResponse(request.method.toUpperCase(), url.pathname, body, onDrift);
            return response;
        },
    };
    const client = createClient({
        baseUrl: options.baseUrl,
        fetch: fetchImpl,
        credentials: 'include',
    });
    client.use(csrfMiddleware, driftMiddleware);
    return client;
}
//# sourceMappingURL=client.js.map